import Decimal from "decimal.js"
import categoryTypeConst from "../../constants/categoryTypeConst"
import sideTypeConst from "../../constants/sideTypeConst"
import dmFancybetOddsTypeConst from "../../constants/dmFancybetOddsTypeConst"

export const handlerResponsesCallback = (callback, responses) => {
  responses.forEach(response => {
    const { key, value } = response

    callback[key](value)
  })
}

export const sleep = (milliseconds: number) => {
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      resolve(timer)
    }, milliseconds)
  })
}

export const copy = <T>(item: T): T => {
  return JSON.parse(JSON.stringify(item))
}

export const capitalizeFirstLetter = (str: string): string => {
  if (str) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }
  return str
}

export const currencyFmt = (val: number, { minimumFractionDigits = 2, maximumFractionDigits = 2 } = {}) => {
  return `${Number(Number(Math.abs(val)).toFixed(2)).toLocaleString("en", {
    minimumFractionDigits,
    maximumFractionDigits
  })}`
}

export const formatPL = (val: number | undefined, currencySymbol?: string) => {
  if (val === undefined) return "- -"
  if (currencySymbol) return val >= 0 ? `${currencySymbol}${currencyFmt(val)}` : `(${currencySymbol}${currencyFmt(val)})`

  return val >= 0 ? currencyFmt(val) : `(${currencyFmt(val)})`
}

export const formatPLWithDollarSign = (val: number) => {
  if (val >= 0) {
    return `$${currencyFmt(val)}`
  }

  return `($${currencyFmt(val)})`
}

export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

export const isIOS = () => {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

export const calculateProfitLoss = ({ odds, stake, categoryType, sideType, isFormat, oddsType = undefined }, isCashOut = false) => {
  const isFancyBet =
    categoryType === categoryTypeConst.FANCYBET || (categoryType === categoryTypeConst.DM_FANCYBET && oddsType === dmFancybetOddsTypeConst.DMFANCYBET)
  const isBookmaker = categoryType === categoryTypeConst.BOOK_MAKER || categoryType === categoryTypeConst.DM_BOOK_MAKER
  const calOdds = isFancyBet || isBookmaker ? Decimal.div(odds, 100) : odds

  let profitLoss

  if (isFancyBet || isBookmaker) {
    const calcPL = Decimal.mul(calOdds, stake).toDP(2)
    profitLoss = sideType === sideTypeConst.LAY ? calcPL.neg() : calcPL
  } else {
    const calc = isCashOut
      ? Decimal.sub(Decimal.mul(calOdds, stake), stake).toDecimalPlaces(4)
      : Decimal.sub(Decimal.mul(calOdds, stake), stake).toDP(2)
    profitLoss = sideType === sideTypeConst.LAY ? calc.neg() : calc
  }

  return isFormat ? formatPL(profitLoss) : profitLoss.toString()
}

export const dateDotFmt = dateStr => {
  return dateStr
    .split("/")
    .map(str => +str)
    .join(".")
}

export const toDP = (num, dp) => {
  if (num === undefined || num === null) return 0
  return new Decimal(num).toDP(dp || 2, Decimal.ROUND_HALF_UP).toNumber()
}

export const floorp = (num, pos) => {
  if (num === undefined || num === null) return 0
  return new Decimal(num).toDP(pos, Decimal.ROUND_FLOOR).toNumber()
}

/**
 * 將下注金額數字格式化為千位分隔符和縮寫格式
 *
 * K 省略符號規則：出現 6 位數時，替換最後 3 位數為 K，並省略小數點
 * M 省略符號規則：出現百萬時，亦即 4 位數 + K，替換 4 位數中的最後 3 位數為 M，並加上 2 位小數點
 *
 * @param {number||undefined} betAmount - 要格式化的數字
 * @returns {string} - 格式化後的字符串
 */
export const formatBettingAmount = (betAmount, maximumFractionDigits = 2) => {
  const millions = 1000000
  const oneHundredThousand = 100000
  const formatOptions = { minimumFractionDigits: 0, maximumFractionDigits }

  if (typeof betAmount !== "number") return ""

  const formattedNumber = `${betAmount}`
  const isGreaterThanMillions = betAmount - millions >= 0

  if (isGreaterThanMillions) {
    const positiveIntegerString = `${betAmount.toFixed(0)}`
    const formattedNumberInMillions = `${(positiveIntegerString.slice(0, positiveIntegerString.length - 4) * 0.01).toFixed(2)}`
    return `${currencyFmt(formattedNumberInMillions)}M` // 轉換後一律顯示到小數點第二位
  }

  const isGreaterThanOneHundredThousand = betAmount - oneHundredThousand >= 0

  if (isGreaterThanOneHundredThousand) {
    const [firstNumber] = formattedNumber.split(".")
    return `${firstNumber.slice(0, firstNumber.length - 3)}K`
  }

  return currencyFmt(formattedNumber, formatOptions)
}

export const hexToRGBA = (hex: string, alpha: string) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const getIdFromApiSiteEventId = (apiSiteEventId: string) => {
  return apiSiteEventId?.replace("sr:match:", "") || null
}

export const appWrapperScrollToTop = (isCustomWebsite: boolean) => {
  const desktopScroll =
    document.getElementsByClassName("layout-desktop__body")[0] ||
    document.getElementsByClassName("layout-fullview__scrollable")[0] ||
    document.getElementsByClassName("layout__scrollable")[0]
  if (document.documentElement.classList.contains("is-desktop") || isCustomWebsite) desktopScroll?.scrollTo({ top: 0 })
  window.scrollTo({ top: 0 })
}
